import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { LocalizedMessage, standardDateNoSecondsOptions } from 'intl/LocalizedComponents';
import _get from 'lodash/get';
import PropTypes from 'prop-types';
// HOCs
import withFormManagement from 'hocs/WithFormManagement';
import withStateReinit from 'hocs/WithStateReinit';
// Core components
import Card from 'components/core/Card';
import DateTimePicker2 from 'components/core/DateTimePicker2/DateTimePicker2';
import TextField from 'components/core/TextField';
// Models
import { sanitizeDesc } from 'models/accessWay';
// Custom
import { cpmOnDemandAccessDataShape, errorShape } from 'constants/PropTypesShapes';
import { dateGenerator, dateFormatter } from 'utils/DateUtils';
import { buildFormErrors, cpmOnDemandAccessUpdateConstraints } from 'utils/Forms';
import FormDialogTemplate from 'components/templates/FormDialogTemplate';
import FormHelper from 'components/misc/FormHelper';
// Utils
import RegexUtils from 'utils/RegexUtils';

class OnDemandEditForm extends React.Component {
  constructor(props) {
    super(props);

    this.handleValidate = this.handleValidate.bind(this);
  }

  componentDidMount() {
    // We need to pass component reference to parent (= DialogItem)
    const { onRef } = this.props;
    onRef(this);
  }

  // Handlers called by parent (= DialogItem)
  handleValidate() {
    const {
      onDemand,
      fields: { begin, licensePlate },
      onErrorsChange,
      onValidate,
      timezone,
    } = this.props;

    const errors = buildFormErrors({ begin, licensePlate }, cpmOnDemandAccessUpdateConstraints);

    // Apply errors
    onErrorsChange(errors);

    // No error => validate form
    if (!errors) {
      const beginInUTC = dateFormatter.toServerFormat(begin, timezone, { seconds: 0 });
      onValidate(onDemand, { begin: beginInUTC, licensePlate });
      return true;
    }

    return false;
  }

  render() {
    const {
      fields: { begin, licensePlate },
      errors,
      onFieldsChange,
      timezone,
    } = this.props;

    // Dates
    const beginDate = dateGenerator.dateFromTimezone(begin, timezone);
    const nowDate = dateGenerator.dateFromTimezone(dateGenerator.now(), timezone);

    return (
      <FormDialogTemplate errors={errors}>
        <div className="row">
          <div className="col-12">
            <div className="d-flex">
              <FormHelper
                elements={[
                  {
                    content: (
                      <LocalizedMessage id="help.onDemand.editBegin" />
                    ),
                  },
                  {
                    content: (
                      <LocalizedMessage id="help.onDemand.editLicensePlate" />
                    ),
                  },
                ]}
              />
              <div style={{ flex: 1 }}>
                <Card>
                  <div className="row">
                    <div className="col-12 col-lg-9 my-3">
                      <DateTimePicker2
                        name="begin"
                        calendarType="begin"
                        clearable
                        hideIcons
                        errorText={_get(errors, 'begin')}
                        maxDate={nowDate}
                        defaultDate={beginDate}
                        date={beginDate}
                        labelDateId="field.onDemandBeginDate"
                        labelHourId="field.onDemandBeginTime"
                        placeholderId="placeholder.start"
                        calendarLabelId="calendarLabel.start"
                        onChange={(newBegin) => onFieldsChange({ begin: newBegin })}
                      />
                    </div>
                    <div className="col-12 col-lg-9">
                      <TextField
                        label={<LocalizedMessage id="field.licensePlate" />}
                        value={licensePlate}
                        required
                        errorText={_get(errors, 'licensePlate')}
                        onChange={(value) => onFieldsChange({
                          licensePlate: sanitizeDesc(value, 'license_plate'),
                        })}
                      />
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </FormDialogTemplate>
    );
  }
}

OnDemandEditForm.propTypes = {
  onDemand: cpmOnDemandAccessDataShape.isRequired,
  operationError: errorShape, // eslint-disable-line react/no-unused-prop-types
  fields: PropTypes.object.isRequired,
  errors: PropTypes.object,
  onFieldsChange: PropTypes.func.isRequired,
  onErrorsChange: PropTypes.func.isRequired,
  onRef: PropTypes.func.isRequired,
  onValidate: PropTypes.func.isRequired,
  timezone: PropTypes.string.isRequired,
};

OnDemandEditForm.defaultProps = {
  operationError: null,
  errors: null,
};

const mapFieldsToProps = {
  begin: {
    defaultValue: null,
    resourceKey: 'begin',
  },
  licensePlate: {
    defaultValue: '',
    resourceKey: 'licensePlate',
  },
};

const mapOwnPropsToResources = (ownProps) => ({
  resourceData: ownProps.onDemand,
  operationError: {
    ...ownProps.operationError,
    messageValuesExtractors: {
      begin: [
        {
          key: 'date',
          pattern: RegexUtils.dateTime,
          setTransform: (value, locOwnProps) => locOwnProps.intl.formatDate(value, standardDateNoSecondsOptions),
        },
      ],
    },
  },
});

const mapStateToProps = (state) => ({
  operationError: state.cpmCarparkOnDemand.update.error,
});

module.exports = compose(
  connect(mapStateToProps),
  withFormManagement(mapFieldsToProps, mapOwnPropsToResources, false),
  withStateReinit(['updateCarparkOnDemand']),
)(OnDemandEditForm);



// WEBPACK FOOTER //
// ./routes/cpm/routes/carparks/assets/onDemand/OnDemandEditForm.js